// 主应用入口页面
import { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { Spin, message as antdMessage, Modal } from "antd";
import { showConfirm } from "./utils/modalUtils";
import { LoadingOutlined } from "@ant-design/icons";
import LoginPage from "./pages/LoginPage/index"; // 引入登录页
import AgentPlaza from "./pages/AgentPlaza/index";
import { stopActiveTypeWriter } from "./pages/Message/MarkdownContent";
import axios from "axios";
import ExportPopup from "@/pages/RecipeCustomization/dialog/ExportPopup";
import {
    PROFILE_REQUIRED_FEATURES,
    getProfileIdentity,
    isSameProfile,
    isDefaultProfile,
    isSelectableProfile,
    buildPersonInfoFromProfile,
    NONE_PROFILE_ID,
} from "./utils/profileUtils";
import { isActiveVipMember } from "./utils/vipUtils";
import { trackRecord } from "./utils/trackUtils";
import { useProfile } from "./hooks/useProfile";
import { useSendMessage } from "./hooks/useSendMessage";
import StreamingTTSController from "./utils/TTS/StreamingTTSController";
import messageTTS from "./utils/TTS/MessageTTS";
import { useHistory } from "./hooks/useHistory";
import { usePageState } from "./hooks/usePageState";
import { usePageNavigation } from "./hooks/usePageNavigation";
import { getCurrentPageRouteMeta } from "./hooks/routeMeta";
import "./styles/pc-adapte.less"
import Sidebar from "./pages/Sidebar/index";
import "./App.less";
import NutritionModal from "./pages/Message/NutritionModal";
import FeatureProfileModal from "./component/FeatureProfileModal";
import "./styles/index.less"
import { Routes, Route, useNavigate } from 'react-router-dom';
import { routes } from './router/Routers';
import ConversationKeepAlive, {
    CONVERSATION_KEEP_ALIVE_ROUTES,
} from './router/ConversationKeepAlive';
import {
    getValidLoginId,
    saveLoginSession,
    saveHasInvite,
    clearAuthStorage,
    initGuestId,
} from "./utils/auth";
import {
    syncWxMiniAuthFromUrl,
    notifyWxMiniLogout,
    redirectToWxMiniLogin,
    redirectToWxMiniLoginAsync,
    isWxMiniProgramEnv,
} from "./utils/miniprogramBridge";
import iconVipTrial from "@/assets/vip/icon-vip-trial.png";
const BasicInfo = lazy(() => import("./pages/BasicInfo/index"));
const MealPlan = lazy(() => import("./pages/MealPlan/index"));
const FoodSearch = lazy(() => import("./pages/FoodSearch/index"));
const NutritionistPage = lazy(() => import("./pages/NutritionistPage/index"));
const NutritionAssessment = lazy(() => import("./pages/NutritionAssessment/index"));
const NutritionManage = lazy(() => import("./pages/NutritionManage/index"));
const SportNutrition = lazy(() => import("./pages/SportNutrition/index"));
const GrowthDevelopment = lazy(() => import("./pages/GrowthDevelopment/index"));
const AgentPage = lazy(() => import("./pages/AgentPage/index"));
const ScalePage = lazy(() => import("./pages/ScalePage/index"));
const UserPage = lazy(() => import("./pages/UserPage/index"));
const RecipeCustomization = lazy(() => import("./pages/RecipeCustomization/index"));
const KetogenicDiet = lazy(() => import("./pages/KetogenicDiet/index"))
const PersonalVIPPage = lazy(() => import("./pages/vip/PersonalVIPPage"));
const MainPage = lazy(() => import("./pages/MainPage/index"));

const EMPTY_FEATURE_PROFILE_MODAL = {
    open: false,
    targetType: "",
    profiles: [],
    selectedKey: "",
};

export default function App() {
    const navigate = useNavigate();
    // --- 状态管理 ---
    const [fileList, setFileList] = useState({ type: "", list: [] }); // 上传文件列表
    const [isAuthorized, setIsAuthorized] = useState(true); // 控制是否显示登录页
    const [isVisitor, setIsVisitor] = useState(true); // 标记是否为游客模式
    const [userId, setUserId] = useState(
        localStorage.getItem("medicalUserId") || "", // 用户ID
    );
    const [featureProfileModal, setFeatureProfileModal] = useState(
        EMPTY_FEATURE_PROFILE_MODAL, // 特征档案弹窗状态
    );
    const [featureProfileLoading, setFeatureProfileLoading] = useState(false); // 特征档案加载态
    const [showExportPopup, setShowExportPopup] = useState(false); // 导出弹窗
    const [isDownloadLoading, setIsDownloadLoading] = useState(false); // 下载加载态
    const [currentPlanCode, setCurrentPlanCode] = useState(""); // 当前导出消息的 planCode
    // 页面导航：currentPage 状态 + 返回栈（统一替代原 featureFromPage / basicInfoFromPage 来源页变量）
    const { currentPage, goTo, goBack, rememberResume, resume } = usePageNavigation("main");
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // 侧边栏是否展开
    const touchStartPos = useRef({ x: 0, y: 0 }); // 触摸起始位置（用于左右滑动手势判断）
    const isHorizontalSwipe = useRef(false); // 是否为水平滑动
    const [isNutritionModalOpen, setIsNutritionModalOpen] = useState(false); // 营养师弹窗是否打开
    const [userPhone, setUserPhone] = useState(
        localStorage.getItem("medicalUserPhone") || "", // 用户手机号
    );
    const [loginTrigger, setLoginTrigger] = useState(0); // 登录校验触发器，小程序一键登录回来后 +1 触发重校验
    // 记录已展示过登录欢迎弹窗的 userId，避免重复弹出；ref 持有最新函数供事件回调使用
    const shownLoginWelcomeForRef = useRef(null);
    const showLoginWelcomeModalRef = useRef(null);
    // === 各对话页面状态（usePageState 统一管理，新增页面仅需一行） ===
    const mainPage = usePageState();
    const agentPage = usePageState();
    const nutritionPage = usePageState();

    // 别名解构 — 只提取顶层直接使用的变量，其余通过 pageConfigs 间接访问
    const {
        messages: mainMessages, setMessages: setMainMessages,
        isLoading: isMainLoading, setIsLoading: setIsMainLoading,
        sessionIdRef: mainSessionIdRef,
        containerRef: messagesContainerRef,
    } = mainPage;
    const {
        messages: agentMessages, setMessages: setAgentMessages,
        isLoading: isAgentLoading, setIsLoading: setIsAgentLoading,
        sessionIdRef: agentSessionIdRef,
        containerRef: agentContainerRef,
    } = agentPage;
    const {
        messages: nutritionMessages, setMessages: setNutritionMessages,
        isLoading: isNutritionLoading,
        sessionIdRef: nutritionSessionIdRef,
        containerRef: nutritionContainerRef,
    } = nutritionPage;
    const handleNewChatRef = useRef(null); // 用于 useHistory 的延时绑定，避免定义顺序问题
    // --- 全局实时语音播报 ---
    const streamingTTSRef = useRef(null);
    const [isTTSEnabled, setIsTTSEnabled] = useState(false);
    const [basicInfoMode, setBasicInfoMode] = useState("list"); // 基本信息展示模式：list | edit
    // --- useProfile Hook：档案与会员状态管理 ---
    const {
        currentProfile,
        setCurrentProfile,
        memberInfo,
        fetchDefaultProfile,
        fetchAllProfiles,
        fetchMemberStatus,
        sidebarVipInfo,
    } = useProfile({ userId, isVisitor, currentPage });

    // --- 档案 ref（供 @update 弹窗回调用，避免闭包过期）---
    const profileNickRef = useRef("孩子");
    const profileIdRef = useRef(null);
    useEffect(() => {
        profileNickRef.current = currentProfile?.profileNick || "孩子";
        profileIdRef.current = currentProfile?.id;
    }, [currentProfile]);

    // --- 记录卡片「查看」通用跳转：切换功能页 + 同步目标日期 + 同步档案 ---
    // 由 DietRecordCard / SportRecordCard 等卡片派发同一事件 open-feature-page，
    // detail: { page: 目标 currentPage, date: 目标日期, dateKey: 日期存储 key, profile: 目标档案 }
    // 注意：dateKey 必须是「跳转定位日期」专用的一次性 key（如 NutritionAssessmentTargetDate），
    // 不要复用功能页自身的浏览日期 key（如 NutritionAssessmentRecordDate），
    // 否则会被对话页写回的浏览日期覆盖，导致跳转后日期不对。
    useEffect(() => {
        const handleOpenFeaturePage = (event) => {
            const { page, date, dateKey, profile } = event?.detail || {};
            // 同步档案：目标页会根据该档案匹配并展示（档案胶囊、数据归属）
            if (profile) setCurrentProfile(profile);
            // 同步目标日期：目标页读取该 key 定位到对应日期
            if (date && dateKey) {
                localStorage.setItem(dateKey, date);
            }
            const isFromConversation = window.location.pathname.includes("/conversation/");
            // 对话页等子路由下需先回到根路由，currentPage 对应的页面才会被渲染
            if (isFromConversation) {
                navigate("/");
            }
            // 通过返回栈记录来源页面，便于目标页返回时回到正确的上一页。
            // 注意：在独立对话页内点击卡片「查看」时，对话页是「透传层」，
            // 此时用 replace 不压栈，沿用进入对话页前记录的来源页，避免返回时卡在当前页或跳错页面。
            if (page) {
                goTo(page, { stack: isFromConversation ? "replace" : "push" });
            }
        };

        window.addEventListener("open-feature-page", handleOpenFeaturePage);
        return () => {
            window.removeEventListener("open-feature-page", handleOpenFeaturePage);
        };
    }, [navigate, setCurrentProfile, goTo]);

    // --- 全局实时语音播报控制器初始化 ---
    useEffect(() => {
        let voice = (currentPage === 'doctor_xu'||currentPage === 'main' ) ? 'cosyvoice-v2-voiceofxu-4df48c8' : "longxiaochun"
        console.log('[App] TTS currentPage:', currentPage, 'selected voice:', voice);
        messageTTS.setVoice(voice);
        // 每个智能体各自独立的 TTS 开关状态（许鹏飞医生 / 智能营养师分别记忆）
        const storageKey =
            currentPage === 'doctor_xu'
                ? 'tts_enabled_doctor_xu'
                : currentPage === 'nutritionist'
                    ? 'tts_enabled_nutritionist'
                    : 'global_streaming_tts_enabled';
        const controller = new StreamingTTSController({ voice: voice, storageKey });
        // 每次页面切换统一重置为不播放，避免沿用上一次的开启状态
        controller.setEnabled(false);
        streamingTTSRef.current = controller;
        setIsTTSEnabled(controller.isEnabled());
        const unsubscribe = controller.addStateListener(({ enabled }) => {
            setIsTTSEnabled(enabled);
        });
        controller.init().catch((err) => {
            console.error("[App] TTS init failed:", err);
        });
        return () => {
            controller.stop();
            messageTTS.stop();
            unsubscribe();
            controller.reset();
            streamingTTSRef.current = null;
        };
    }, [currentPage]);

    const toggleStreamingTTS = useCallback(() => {
        const enabled = streamingTTSRef.current?.toggle();
        if (enabled) {
            antdMessage.success("开启自动朗读");
        } else {
            antdMessage.info("关闭自动朗读");
        }
    }, []);

    // --- 档案解析回调（供 useSendMessage 发送时获取 profileCode 和 personInfo）---
    const resolveProfile = useCallback(async (_isNutri) => {
        let profile = currentProfile;
        // 未选中档案时兜底拉取默认档案
        if (!profile) {
            profile = await fetchDefaultProfile(userId);
        }
        if (!profile || profile.id === NONE_PROFILE_ID) {
            return { profileCode: "", personInfo: null };
        }
        return {
            profileCode: profile.profileCode || "",
            personInfo: buildPersonInfoFromProfile(profile),
        };
    }, [currentProfile, userId, fetchDefaultProfile]);

    // --- 各页面配置（只需指定 agentId，其余字段由 usePageState 统一提供）---
    const pageConfigs = {
        main: { ...mainPage, agentId: 0 },
        agent: { ...agentPage, agentId: 1 },
        doctor_xu: { ...agentPage, agentId: 1 },
        nutritionist: { ...nutritionPage, agentId: 2 },
    };

    const currentPageConfig = pageConfigs[currentPage]

    // --- useSendMessage Hook：对话发送 + 流式处理 + 滚动控制 ---
    const {
        handleSendMessage: _handleSendMessage,
        scrollToBottom,
        showScrollToBottom,
        checkScrollPosition,
        buttonBottom,
    } = useSendMessage({
        ...currentPageConfig,
        userId,
        resolveProfile,
        streamingTTSRef,
        profileNickRef,
        profileIdRef,
        onProfileUpdateRequest: () => {
            setBasicInfoMode("edit");
            goTo("basic_info");
        },
        onQuotaUpgrade: () => {
            // 配额用尽引导开通：按原语义固定返回首页
            goTo("vip_renew", { from: "main" });
        },
    });

    // 包装 handleSendMessage：发送消息前校验登录态
    const handleSendMessage = useCallback(async (input, fileList, personInfo, options) => {
        if (isVisitor) {
            showConfirm("该功能需要登录后使用，是否立即登录？", "去登录", () => {
                if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页，不展示 H5 登录页
                rememberResume();
                setIsAuthorized(false);
            });
            return;
        }
        // if ((memberInfo?.memberStatus || memberInfo?.status) !== "ACTIVE") {
        //     showConfirm("该功能为会员专属，开通后即可使用", "去开通", () => goTo("vip_renew"));
        //     return;
        // }
        await _handleSendMessage(input, fileList, personInfo, options);
    }, [isVisitor, rememberResume, _handleSendMessage]);

    // 语音模式切换前校验登录态
    const handleBeforeVoiceToggle = useCallback(async () => {
        if (isVisitor) {
            showConfirm("该功能需要登录后使用，是否立即登录？", "去登录", () => {
                if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页，不展示 H5 登录页
                rememberResume();
                setIsAuthorized(false);
            });
            return false;
        }
        // if ((memberInfo?.memberStatus || memberInfo?.status) !== "ACTIVE") {
        //     showConfirm("该功能为会员专属，开通后即可使用", "去开通", () => goTo("vip_renew"));
        //     return false
        // }
        return true;
    }, [isVisitor, rememberResume]);

    const handleStopTTS = useCallback(() => {
        streamingTTSRef.current?.stop();
        messageTTS.stop();
    }, []);

    // AI分身（doctor_xu）需要登录 + 会员身份
    const handleGoToDoctorXu = useCallback(() => {
        if (isVisitor) {
            showConfirm("该功能需要登录后使用，是否立即登录？", "去登录", () => {
                if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页，不展示 H5 登录页
                rememberResume();
                setIsAuthorized(false);
            });
            return;
        }
        if ((memberInfo?.memberStatus || memberInfo?.status) !== "ACTIVE") {
            showConfirm("该功能为会员专属，开通后即可使用", "去开通", () => goTo("vip_renew"));
            return;
        }
        goTo("doctor_xu");
    }, [isVisitor, memberInfo, goTo, rememberResume]);

    // --- useHistory Hook：历史记录管理 ---
    const {
        sidebarHistory,
        setSidebarHistory,
        hadHisList,
        isHisLoading,
        isFetchingMore,
        hasMoreHistory,
        fetchHistoryList,
        handleSelectHistory,
        handleDeleteSession,
    } = useHistory({
        handleNewChatRef,
        mainMessages,
        stopActiveTypeWriter,
        scrollToBottom,
        setMainMessages,
        setAgentMessages,
        setNutritionMessages,
        goTo,
        setIsSidebarOpen,
        mainSessionIdRef,
        agentSessionIdRef,
        nutritionSessionIdRef,
    });

    useEffect(() => {
        const routeMeta = getCurrentPageRouteMeta({
            currentPage,
            isAuthorized,
        });

        console.log(
            "[页面跳转]",
            `页面标识：${routeMeta.routeKey}`,
            `功能名称：${routeMeta.featureName}`,
            `组件名称：${routeMeta.componentName}`,
            `文件路径：${routeMeta.filePath}`,
        );
    }, [currentPage, isAuthorized]);

    // 1. 优化用户名计算逻辑
    const displayUsername = useMemo(() => {
        // 优先判断是否是正式登录态且有手机号
        const loginStatus = localStorage.getItem("loginStatus");
        if (loginStatus === "logged_in" && userPhone) {
            return `用户_${userPhone.slice(-4)}`;
        }
        return "游客用户";
    }, [userPhone]); // 依赖 userPhone 变化自动更新

    // 初始化字体偏移量，从 localStorage 读取，默认为 0
    const [fontOffset, setFontOffset] = useState(0);
    const handleGoToSettings = () => {
        setBasicInfoMode("list");
        goTo("basic_info"); // 自动记录来源页，返回时回到正确页面
    };

    // 监听偏移量变化，动态更新 CSS 变量
    useEffect(() => {
        // 将 --font-offset 变量挂载在 HTML 根节点上
        document.documentElement.style.setProperty(
            "--font-offset",
            `${fontOffset}px`,
        );
        localStorage.setItem("globalFontOffset", fontOffset);
    }, [fontOffset]);

    // 3. 计算当前是否处于"关怀版" (只要偏移量大于 0 就视为关怀版)
    const isCareMode = fontOffset > 0;

    // 4. 处理切换逻辑
    const handleToggleCareMode = () => {
        if (isCareMode) {
            setFontOffset(0); // 切换回标准版
            antdMessage.success("已切换至标准版");
        } else {
            setFontOffset(3); // 开启关怀版，默认放大 3px
            antdMessage.success("已开启关怀版，字体已为您放大");
        }
    };

    // 处理智能体选择（含登录/会员权限校验）
    const handleAgentSelect = async (type) => {
        const protectedRoutes = [
            "basic_info",
            "meal_plan",
            "food_query",
            "assessment",
            "nutrition_manage",
            "recipe_customization",
            "ketogenic_diet",
            "sport_nutrition",
            "growth_development",
            "nutritionist",
        ];
        if (protectedRoutes.includes(type) && type !== "food_query" && isVisitor) {
            showConfirm("该功能需要登录后使用，是否立即登录？", "去登录", () => {
                if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页，不展示 H5 登录页
                rememberResume();
                setIsAuthorized(false);
            });
            return;
        }

        if ((type === "growth_development" || type === "ketogenic_diet")
            && (memberInfo?.memberStatus || memberInfo?.status) === "ACTIVE" && memberInfo?.memberLevel !== "GOLD") {
            showConfirm("该功能为黄金会员专属，开通后即可使用", "去开通", () => goTo("vip_renew"));
            return;
        } else if ((memberInfo?.memberStatus || memberInfo?.status) !== "ACTIVE") {
            showConfirm("该功能为会员专属，开通后即可使用", "去开通", () => goTo("vip_renew"));
            return;
        }

        if (PROFILE_REQUIRED_FEATURES[type]) {
            await handleProfileFeatureEntry(type);
            return;
        }

        // 权限校验通过后的跳转逻辑（统一走 goTo 压入返回栈，目标页 onBack 可回到来源页）
        if (type === "basic_info") {
            setBasicInfoMode("list");
            goTo("basic_info");
        } else if (type === "meal_plan") {
            goTo("meal_plan");
        } else if (type === "food_query") {
            goTo("food_query");
        } else if (type === "nutritionist") {
            goTo("nutritionist");
        } else if (type === "assessment") {
            goTo("nutrition_assessment");
        } else if (type === "nutrition_manage") {
            goTo("nutrition_manage");
        } else if (type === "sport_nutrition") {
            goTo("sport_nutrition");
        } else if (type === "growth_development") {
            goTo("growth_development");
        } else if (type === "recipe_customization") {
            goTo("recipe_customization");
        } else if (type === "ketogenic_diet") {
            goTo("ketogenic_diet");
        } else {
            console.log("即将跳转至功能模块:", type);
        }
    };

    useEffect(() => {
        // 若子页面明确要求回到登录页，强制展示 LoginPage
        const forceShowLogin = localStorage.getItem("forceShowLogin") === "1";
        if (forceShowLogin) {
            localStorage.removeItem("forceShowLogin");
            // 小程序环境：跳转小程序原生登录页，不展示 H5 登录页
            (async () => {
                if (await redirectToWxMiniLoginAsync()) return;
                clearAllAuthData();
                // 生成新的游客ID（与正常游客模式逻辑一致）
                const guestId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                localStorage.setItem("medicalUserId", guestId);
                localStorage.setItem("loginStatus", "guest");
                rememberResume();
                setIsAuthorized(false);
                setIsVisitor(true);
                setUserId(guestId);
                setUserPhone("");
                fetchHistoryList(false);
            })();
            return;
        }

        // 优先同步小程序 URL 登录参数（source=wxmini），确保 localStorage 为最新
        const wxMiniSyncResult = syncWxMiniAuthFromUrl();

        const validLoginId = getValidLoginId();
        const storedPhone = localStorage.getItem("medicalUserPhone");
        console.log(">>> [会话校验] wxMiniSyncResult:", wxMiniSyncResult, "| validLoginId:", validLoginId, "| storedPhone:", storedPhone);

        if (validLoginId) {
            // 登录态有效（loginStatus=logged_in 且未过期即视为登录，phone 可能为空）
            setIsAuthorized(true);
            setIsVisitor(false);
            setUserId(validLoginId);
            setUserPhone(storedPhone || "");
            console.log(">>> [会话校验] 登录态有效，已自动续期");

            // 页面挂载初始化时，立即调取接口查询默认人员信息，确保首页首屏渲染
            fetchDefaultProfile(validLoginId, false);

            // 小程序一键登录后新登录：展示欢迎弹窗（未成年人提示 + VIP 体验卡）
            if (
                wxMiniSyncResult &&
                wxMiniSyncResult.loginStatus === "logged_in" &&
                wxMiniSyncResult.isNewLogin &&
                shownLoginWelcomeForRef.current !== wxMiniSyncResult.userId
            ) {
                console.log(">>> [会话校验] 满足小程序新登录弹窗条件，触发欢迎弹窗:", wxMiniSyncResult);
                shownLoginWelcomeForRef.current = wxMiniSyncResult.userId;
                showLoginWelcomeModalRef.current?.(wxMiniSyncResult.newUserFreeTrial, wxMiniSyncResult.freeTrialDays);
            }
        } else {
            // 游客模式兜底逻辑保持不变...
            console.log(">>> [会话校验] 进入游客模式");
            clearAllAuthData();
            const currentId = localStorage.getItem("medicalUserId");
            if (!currentId || !currentId.startsWith("user_")) {
                const guestId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                localStorage.setItem("medicalUserId", guestId);
                localStorage.setItem("loginStatus", "guest");
                setUserId(guestId);
            } else {
                setUserId(currentId);
            }
        }

        fetchHistoryList(false);
    }, [fetchDefaultProfile, loginTrigger, rememberResume]); // 💡 补全依赖项 + loginTrigger 支持小程序一键登录后重校验

    // 【小程序一键登录】监听页面可见性变化，自动重新同步登录态
    // 场景：小程序 WebView 已打开的情况下，用户完成一键登录后，
    //       小程序重新加载 WebView（带新登录参数），H5 自动检测并同步
    useEffect(() => {
        const handlePageVisible = () => {
            if (document.visibilityState !== 'visible') return;
            const result = syncWxMiniAuthFromUrl();
            if (result) {
                console.log('>>> [MiniProgram] 页面可见，检测到登录参数变化，同步登录态:', result.loginStatus);
                // 触发登录态重校验，主 useEffect 会重新读取 localStorage 中的新登录信息
                setLoginTrigger(v => v + 1);
                // 若当前处于未授权状态且新结果是 logged_in，立即切换状态
                if (result.loginStatus === 'logged_in') {
                    setUserId(result.userId);
                    setUserPhone(result.phone);
                    setIsAuthorized(true);
                    setIsVisitor(false);
                    // 新登录（URL 中 userId 与本地不同）：与 H5 登录一致，展示欢迎弹窗
                    if (result.isNewLogin && shownLoginWelcomeForRef.current !== result.userId) {
                        shownLoginWelcomeForRef.current = result.userId;
                        showLoginWelcomeModalRef.current?.(result.newUserFreeTrial, result.freeTrialDays);
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handlePageVisible);
        window.addEventListener('pageshow', handlePageVisible);
        // 微信小程序 web-view 从原生页返回时 visibilitychange 可能不触发，补监听 focus
        window.addEventListener('focus', handlePageVisible);

        return () => {
            document.removeEventListener('visibilitychange', handlePageVisible);
            window.removeEventListener('pageshow', handlePageVisible);
            window.removeEventListener('focus', handlePageVisible);
        };
    }, []);

    //  监听侧边栏的打开状态。每当用户点击左上角菜单打开侧边栏时，
    //  立即重新获取第一页历史记录，确保看到的是包含本次咨询在内的最新数据。
    useEffect(() => {
        if (isSidebarOpen) {
            console.log(">>> [侧边栏打开] 自动刷新最新历史记录");
            fetchHistoryList(false);
        }
    }, [isSidebarOpen]);
    // 封装侧边栏打开逻辑，统一处理埋点
    const handleOpenSidebar = () => {
        if (!isSidebarOpen) {
            trackRecord(userId, "OPEN_SIDEBAR", "唤起侧边栏");
            setIsSidebarOpen(true);
        }
    };

    const navigateToProfileFeature = useCallback((type) => {
        if (type === "meal_plan") {
            goTo("meal_plan");
        } else if (type === "assessment") {
            goTo("nutrition_assessment");
        } else if (type === "nutrition_manage") {
            goTo("nutrition_manage");
        } else if (type === "recipe_customization") {
            goTo("recipe_customization");
        } else if (type === "sport_nutrition") {
            goTo("sport_nutrition");
        } else if (type === "growth_development") {
            goTo("growth_development");
        } else if (type === "ketogenic_diet") {
            goTo("ketogenic_diet");
        }
    }, [goTo]);

    const handleProfileFeatureEntry = useCallback(async (type) => {
        if (!PROFILE_REQUIRED_FEATURES[type] || featureProfileLoading) return;

        const uid = localStorage.getItem("medicalUserId") || userId;
        if (!uid) {
            antdMessage.warning("用户信息获取中，请稍后再试");
            return;
        }

        setFeatureProfileLoading(true);
        try {
            const profiles = (await fetchAllProfiles(uid)) || [];

            fetchMemberStatus();

            const selectableProfiles = profiles.filter(isSelectableProfile);

            if (selectableProfiles.length === 0) {
                showConfirm("当前没有可用的儿童档案，请先新增或维护档案信息。", "去完善", () => {
                    setBasicInfoMode(profiles.length > 0 ? "list" : "add");
                    // 按原语义固定返回智能体页
                    goTo("basic_info", { from: "agent" });
                });
                return;
            }

            if (profiles.length === 1) {
                setCurrentProfile(selectableProfiles[0]);
                navigateToProfileFeature(type);
                return;
            }

            const preferredProfile =
                selectableProfiles.find(isDefaultProfile) ||
                selectableProfiles.find((profile) =>
                    isSameProfile(profile, currentProfile),
                ) ||
                selectableProfiles[0];

            setFeatureProfileModal({
                open: true,
                targetType: type,
                profiles,
                selectedKey: getProfileIdentity(preferredProfile),
            });
        } catch (error) {
            console.error("进入功能前加载儿童档案失败", error);
            antdMessage.error("儿童档案加载失败，请稍后再试");
        } finally {
            setFeatureProfileLoading(false);
        }
    }, [currentProfile, featureProfileLoading, navigateToProfileFeature, userId, fetchAllProfiles, fetchMemberStatus, setCurrentProfile, goTo]);

    const handleConfirmFeatureProfile = () => {
        const selectedProfile = featureProfileModal.profiles.find(
            (profile) =>
                getProfileIdentity(profile) === featureProfileModal.selectedKey &&
                isSelectableProfile(profile),
        );

        if (!selectedProfile) {
            antdMessage.warning("请选择可用的儿童档案");
            return;
        }

        const targetType = featureProfileModal.targetType;
        setCurrentProfile(selectedProfile);
        setFeatureProfileModal(EMPTY_FEATURE_PROFILE_MODAL);
        navigateToProfileFeature(targetType);
    };

    // 修改 clearAllAuthData 以调用工具函数
    const clearAllAuthData = () => {
        clearAuthStorage(); // 清除 localStorage
        setUserId("");
        setUserPhone("");
        setIsAuthorized(true);
        setIsVisitor(true);
    };

    /**
     * 登录成功/UserId同步逻辑
     */
    const handleAuthFinish = (newUserId, phone, hasInvite) => {
        setMainMessages([]);
        setAgentMessages([]);
        setNutritionMessages([]); // 清空上一账号在智能营养师页面的咨询记录
        setCurrentProfile(null);
        stopActiveTypeWriter();
        setIsSidebarOpen(false);

        // 【修改点】：使用统一工具保存，包含过期时间戳
        saveLoginSession(newUserId, phone);
        // 登录接口返回的「是否有邀请资格」，供邀请相关入口读取
        saveHasInvite(hasInvite);

        setUserId(newUserId);
        setUserPhone(phone);
        setIsVisitor(false);
        setIsAuthorized(true);

        fetchHistoryList(false);
        // 登录完成，回到登录前所在的页面
        resume();

        // 若有来源页面记录（如子路由触发的登录），登录成功后回跳
        const returnUrl = localStorage.getItem("loginReturnUrl");
        if (returnUrl) {
            localStorage.removeItem("loginReturnUrl");
            navigate(returnUrl);
        }
    };

    // 4. 新增退出登录逻辑
    const handleLogout = () => {
        // 1. 弹出确认框
        Modal.confirm({
            title: "确认退出",
            content: "您确定要退出当前登录状态吗？",
            okText: "确认退出",
            cancelText: "取消",
            centered: true,
            okButtonProps: { danger: true }, // 红色按钮表示危险操作
            onOk: async () => {
                try {
                    // 2. 调用退出接口
                    const { data } = await axios.post(
                        `${window.PUBLIC_URL}/medagent/robot/logout`,
                        {
                            userId: userId,
                            phone: userPhone,
                        },
                    );

                    if (data.code === "200" || data.code === 200) {
                        // 3. 清除本地持久化登录信息
                        localStorage.removeItem("loginStatus");
                        localStorage.removeItem("medicalUserId");
                        localStorage.removeItem("medicalUserPhone");
                        
                        clearAllAuthData();
                        notifyWxMiniLogout();

                        // 4. 重置内存中的状态，触发 UI 更新
                        setMainMessages([]); // 清空对话，防止信息泄露
                        setAgentMessages([]);
                        setNutritionMessages([]); // 登出时同步清空智能营养师对话，防止隐私泄露
                        // 清空各页面 sessionIdRef，防止退出后新会话复用旧用户会话 ID
                        mainSessionIdRef.current = "";
                        agentSessionIdRef.current = "";
                        nutritionSessionIdRef.current = "";
                        setCurrentProfile(null);
                        const gid = initGuestId();

                        // 3. 重置 React 状态
                        setUserId(gid);
                       localStorage.setItem("medicalUserId", gid);
                     if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页，不展示 H5 登录页
                        // 登出后清空返回栈，避免换账号后残留上一账号的页面路径
                        goTo("main", { stack: "clear" });

                        antdMessage.success("已安全退出登录");

                        // 可选：为了彻底清除可能存在的缓存或闭包引用，可以执行刷新
                        // window.location.reload();
                    } else {
                        antdMessage.error(data.msg || "退出失败，请重试");
                    }
                } catch (err) {
                    console.error("Logout error:", err);
                    antdMessage.error("网络异常，退出失败");
                }
            },
            onCancel() {
                console.log("取消退出操作");
            },
        });
    };

    // 未成年人提示弹窗取消时，直接退出登录（不再弹二次确认）
    const handleLogoutDirect = async () => {
        try {
            const storedUserId = localStorage.getItem("medicalUserId");
            const storedPhone = localStorage.getItem("medicalUserPhone");
            const { data } = await axios.post(
                `${window.PUBLIC_URL}/medagent/robot/logout`,
                {
                    userId: storedUserId || userId,
                    phone: storedPhone || userPhone,
                },
            );

            if (data.code === "200" || data.code === 200) {
                localStorage.removeItem("loginStatus");
                localStorage.removeItem("medicalUserId");
                localStorage.removeItem("medicalUserPhone");

                clearAllAuthData();
                notifyWxMiniLogout();

                setMainMessages([]);
                setAgentMessages([]);
                setNutritionMessages([]);
                // 清空各页面 sessionIdRef，防止退出后新会话复用旧用户会话 ID
                mainSessionIdRef.current = "";
                agentSessionIdRef.current = "";
                nutritionSessionIdRef.current = "";
                setCurrentProfile(null);
                const gid = initGuestId();

                setUserId(gid);
                localStorage.setItem("medicalUserId", gid);
                if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页，不展示 H5 登录页（先跳转，避免 setIsAuthorized(false) 触发 H5 登录页闪现）
                setIsAuthorized(false);
                // 登出后清空返回栈，避免换账号后残留上一账号的页面路径
                goTo("login", { stack: "clear" });

                antdMessage.success("已安全退出登录");
            } else {
                antdMessage.error(data.msg || "退出失败，请重试");
            }
        } catch (err) {
            console.error("Logout error:", err);
            antdMessage.error("网络异常，退出失败");
        }
    };

    /**
     * 登录成功后的欢迎弹窗：未成年人使用提示 → VIP 体验卡
     * H5 登录（LoginPage.onFinish）与小程序“新登录”（URL 同步）共用
     */
    const showLoginWelcomeModal = (newUserFreeTrial, freeTrialDays) => {
        Modal.confirm({
            title: "使用温馨提示",
            content: "未成年人应当在监护人陪同、监护下使用本服务；若为未成年人，请勿独立使用。",
            okText: "我已阅读并知晓",
            cancelText: '放弃使用',
            centered: true,
            maskClosable: false,
            okButtonProps: {
                style: {
                    backgroundColor: "#5B69FC",
                    borderColor: "#5B69FC",
                },
            },
            onOk: () => {
                 if (newUserFreeTrial === 1) {
                Modal.info({
                    icon: null,
                    title: null,
                    content: (
                        <div className="vip-trial-card">
                            <img
                                src={iconVipTrial}
                                alt="会员体验卡"
                                className="vip-trial-img"
                            />
                            <div className="vip-trial-text">
                                超值{freeTrialDays || 7}天黄金会员体验卡
                            </div>
                            <div
                                onClick={() => Modal.destroyAll()}
                                className="vip-trial-btn"
                            >
                                开心收下
                            </div>
                        </div>
                    ),
                    closable: false,
                    centered: true,
                    maskClosable: false,
                   // width: 270,
                    footer: null,
                    className: 'vip-trial-modal',
                });
                  }
            },
            onCancel: () => {
                // 用户点击"取消退出"，执行退出登录逻辑
                handleLogoutDirect();
            },
        });
    };
    // 同步最新函数引用，供小程序新登录回调（effect / visibilitychange）使用
    showLoginWelcomeModalRef.current = showLoginWelcomeModal;

    /**
     * 量表自测权限拦截逻辑
     */
    const handleOpenScale = () => {
        if (isVisitor) {
            showConfirm("需要登录后才可以，是否立即登录？", "去登录", () => {
                if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页，不展示 H5 登录页
                rememberResume();
                setIsAuthorized(false);
            });

        }
        //  else if ((memberInfo?.memberStatus || memberInfo?.status) !== "ACTIVE") {
        //     showConfirm("该功能为会员专属，开通后即可使用", "去开通", () => {
        //         goTo("vip_renew");
        //     });
        //     return;
        // }

        else {
            goTo("scale"); // 自动记录来源页，返回时跳回正确页面
        }
    };
    const handleNutritionCancel = () => {
        Modal.confirm({
            title: "提示",
            content: "未录入信息将无法生成专属食谱方案，建议您完成信息录入哦~",
            okText: "确认离开",
            cancelText: "继续录入",
            centered: true,
            // 点击"确定"才会真正关闭弹窗
            onOk: () => {
                console.log(">>> [交互日志] 用户确认关闭营养弹窗，终止后续对话逻辑");
                setIsNutritionModalOpen(false);
            },
            // 点击"取消"则什么也不做，弹窗保持开启
            onCancel: () => {
                console.log(">>> [交互日志] 用户取消关闭，继续填写信息");
            },
        });
    };
    const STATIC_REPORT_MESSAGES = [
        {
            id: `report_q_${Date.now()}`,
            text: "报告解读",
            sender: "user",
            isStaticReportMsg: true,
        },
        {
            id: `report_a_${Date.now()}`,
            text: "报告解读引导卡片",
            sender: "assistant",
            isStaticReportMsg: true,
            needTypeWriter: 2,
        },
    ];

    const handleOpenReport = (isAgent = false) => {
        stopActiveTypeWriter();
        // 根据来源决定更新哪个消息列表
        const setter = isAgent ? setAgentMessages : setMainMessages;
        setter((prev) => [...prev, ...STATIC_REPORT_MESSAGES]);

        setTimeout(() => {
            // 根据来源滚动对应的容器
            const containerRef = isAgent ? agentContainerRef : messagesContainerRef;
            if (containerRef.current) {
                containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
        }, 150);
    };

    const handleNutritionSubmit = (values) => {
        // 1. 基础信息拼接：性别、年龄、身高、体重
        const baseInfo = [
            `${values.gender === "男" ? "男孩" : "女孩"}`,
            `${values.age}岁${values.month}个月`,
            `身高${values.height}cm`,
            `体重${values.weight}kg`,
        ];

        // 2. 拼接健康诉求 (如果有)
        if (values.health_need) {
            baseInfo.push(values.health_need);
        }

        // 3. 拼接已知疾病 (如果有且不为空则拼接，否则不处理)
        if (values.tell_disease && values.tell_disease.trim()) {
            baseInfo.push(values.tell_disease);
        }

        // 4. 使用"，"组合成最终发送给 AI 的文本
        const personalizedMessage = baseInfo.join("，");

        // 构造结构化数据对象
        const personInfoObj = {
            age: values.age,
            month: values.month,
            gender: values.gender,
            height: values.height,
            weight: values.weight,
            health_need: values.health_need,
            tell_disease: values.tell_disease,
        };

        // 关闭弹窗
        setIsNutritionModalOpen(false);

        // 【核心优化】：发送拼接后的个性化文本，而非固定的"营养干预"
        console.log(">>> [营养表单提交] 个性化文案:", personalizedMessage);
        handleSendMessage(personalizedMessage, null, personInfoObj);
    };

    /**
     * 下载回调：流式输出中包含 @download 标记时，点击下载图标触发
     * 校验 planCode 后弹出导出格式选择弹窗（Excel / 图片）
     */
    const handleDownload = useCallback((planCode) => {
        if (!planCode) {
            antdMessage.info("数据生成中，请稍后...");
            return;
        }
        // 小程序环境且 JS-SDK 已就绪：跳转到小程序原生下载页面（pages/download/download），由小程序侧完成文件下载
        // 同步判断，不等待 SDK 加载；SDK 未就绪时直接降级为 H5 导出弹窗
        if (isWxMiniProgramEnv() && window.wx?.miniProgram) {
            // 跳转到小程序前关闭正在进行的语音播报
            streamingTTSRef.current?.stop();
            messageTTS.stop();
            window.wx.miniProgram.navigateTo({
                url: `/pages/download/download?planCode=${encodeURIComponent(
                    planCode
                )}`
            });
            return;
        }
        setCurrentPlanCode(planCode);
        setShowExportPopup(true);
    }, []);

    /**
     * 会员引导回调：流式输出中包含 @silvervip / @goldvip 标记时，点击"去开通"触发
     * 记录来源页面后跳转会员中心（vip_renew）
     */
    const handleVipGuide = useCallback((level) => {
        // level: "silver" | "gold"
        goTo("vip_renew");
    }, [goTo]);

    /**
     * 下载执行函数：调用导出接口下载 Excel 或图片
     */
    const download = useCallback(async (type) => {
        setShowExportPopup(false);
        setIsDownloadLoading(true);
        try {
            const { data } = await axios.post(
                `${window.PUBLIC_URL}/medagent/recipe/custom/exportRecipes`,
                { planCode: currentPlanCode, fileType: type },
                { responseType: "blob" }
            );

            if (data.code === 4001) {
                setIsDownloadLoading(false);
                return antdMessage.info(data.msg);
            }

            let targetBlob;
            if (type === "xlsx") {
                targetBlob = new Blob([data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                });
            } else if (type === "png") {
                targetBlob = new Blob([data], { type: "image/png" });
            }

            const aDom = document.createElement("a");
            aDom.href = URL.createObjectURL(targetBlob);
            aDom.download = `食谱导出.${type}`;
            document.body.appendChild(aDom);
            aDom.click();
            document.body.removeChild(aDom);
            URL.revokeObjectURL(aDom.href);
        } catch (err) {
            console.error("导出失败", err);
            antdMessage.error("导出失败，请稍后再试");
        } finally {
            setIsDownloadLoading(false);
        }
    }, [currentPlanCode]);

    const onFeedback = (params) => {
        const currentSetter =
            currentPage === "main"
                ? setMainMessages
                : currentPage === "nutritionist"
                    ? setNutritionMessages
                    : setAgentMessages;

        currentSetter((prev) =>
            prev.map((msg) =>
                msg.requestId === params.requestId
                    ? { ...msg, feedback: params.feedback }
                    : msg,
            ),
        );
    };

    const handleTry = (id) => {
        // 完善上下文判断，增加 nutritionist 逻辑
        const context =
            currentPage === "main"
                ? { msgs: mainMessages, setMsgs: setMainMessages }
                : currentPage === "nutritionist"
                    ? { msgs: nutritionMessages, setMsgs: setNutritionMessages } // 新增此行
                    : { msgs: agentMessages, setMsgs: setAgentMessages };

        if (context.msgs.length < 2) return;
        const secondLastMsg = context.msgs.at(-2);
        stopActiveTypeWriter();
        context.setMsgs((prev) => prev.slice(0, -2));
        handleSendMessage(secondLastMsg.text);
    };
    const handleNewChat = (page) => {
        const targetPage = typeof page === "string" ? page : currentPage;
        const config = pageConfigs[targetPage];
        if (!config) return;

        streamingTTSRef.current?.stop();
        messageTTS.stop();
        config.sessionIdRef.current = "";
        config.setMessages([]);
        handleStop(targetPage);
        config.setIsLoading(false);
    };

    // 将 handleNewChat 注入到 useHistory 的 ref 中（每渲染周期更新，保证引用最新）
    handleNewChatRef.current = handleNewChat;

    const handleStop = (page) => {
        const targetPage = page || currentPage;
        const config = pageConfigs[targetPage];
        if (!config) return;

        if (config.thinkingTimerRef?.current) {
            clearInterval(config.thinkingTimerRef.current);
            config.thinkingTimerRef.current = null;
        }
        if (config.abortRef?.current) {
            config.abortRef.current.abort();
        } else {
            stopActiveTypeWriter();
        }
    };

    const handleFaqTypeWriter = (faqText = "") => {
        const isMain = currentPage === "main";
        const setMsgs = isMain ? setMainMessages : setAgentMessages;
        const currentLoadingSetter = isMain ? setIsMainLoading : setIsAgentLoading;

        currentLoadingSetter(false); // 关闭当前页面对应的 Loading
        setMsgs((prev) =>
            prev.map((msg, i) =>
                i === prev.length - 1
                    ? {
                        ...msg,
                        loading: false,
                        text: faqText || msg.text,
                        needTypeWriter: 2,
                    }
                    : msg,
            ),
        );
    };


    return (
        <div
            className="dialog-container"
        >

            {/* 对话页保活容器：常驻挂载（在 <Routes> 之外），离开对话页时只隐藏不卸载，
                从而保留 ChatArea 的消息 / 输入框 / 滚动位置 / 语音播报等状态 */}
            <ConversationKeepAlive />

            <Routes>
                {routes.map((route) => (
                    <Route key={route.path} path={route.path} element={route.element} />
                ))}

                {/* 4 个对话页的路由占位：仅用于让路由命中这些 path（避免落到 * 兜底路由），
                    实际页面由上方 ConversationKeepAlive 常驻渲染 */}
                {CONVERSATION_KEEP_ALIVE_ROUTES.map((route) => (
                    <Route key={route.path} path={route.path} element={null} />
                ))}

                <Route path="*" element={
                    !isAuthorized ? (
                        // 登录页面
                        <LoginPage
                            userId={userId}
                            onFinish={(id, newUserFreeTrial, _freeTrialDays, phone, hasInvite) => {
                                // 此处必须显式接收并传递 phone / hasInvite（接口返回的是数字 1/0）
                                localStorage.setItem("loginStatus", "logged_in");
                                handleAuthFinish(id, phone, hasInvite);
                                // 欢迎弹窗（未成年人提示 + VIP 体验卡），与小程序新登录逻辑共用
                                showLoginWelcomeModal(newUserFreeTrial, _freeTrialDays);
                            }}
                            onVisitor={() => {
                                console.log(">>> [切换模式] 进入游客模式");

                                // 1. 彻底清理旧的正式用户信息和消息上下文
                                localStorage.setItem("loginStatus", "guest");
                                setMainMessages([]);
                                setAgentMessages([]);
                                setNutritionMessages([]); // 登出时同步清空智能营养师对话，防止隐私泄露
                                setCurrentProfile(null);
                                setSidebarHistory([]); // 【新增】：显式清空侧边栏历史，防止数据残留
                                stopActiveTypeWriter();

                                setIsSidebarOpen(false);

                                // 若有来源页面记录（如子路由触发的登录），游客模式直接回跳
                                const visitorReturnUrl = localStorage.getItem("loginReturnUrl");
                                if (visitorReturnUrl) {
                                    localStorage.removeItem("loginReturnUrl");
                                    setUserId(userId);
                                    setIsVisitor(true);
                                    setIsAuthorized(true);
                                    setTimeout(() => {
                                        navigate(visitorReturnUrl);
                                    }, 0);
                                    return;
                                }

                                // 游客模式切换完成，回到切换前所在的页面
                                resume();

                                // 2. 强制纠正 ID：如果当前本地存的是正式账号，必须更换为游客 ID
                                const currentId = localStorage.getItem("medicalUserId");
                                console.log("!currentId", !currentId);
                                // console.log("!currentId.startsWith('user_')",!currentId.startsWith('user_'));

                                if (!currentId || !currentId.startsWith("user_")) {
                                    const newGuestId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                                    localStorage.setItem("medicalUserId", newGuestId);
                                    setUserId(newGuestId);
                                }

                                setIsVisitor(true);
                                setIsAuthorized(true);

                                // 3. 异步获取属于当前游客 ID 的历史
                                // 由于 fetchHistoryList 内部读取的是 localStorage，确保上面 setItem 已经完成
                                setTimeout(() => {
                                    fetchHistoryList(false);
                                }, 0);
                            }}
                        />
                    ) : // 2. 授权通过后的主逻辑
                        currentPage === "main" ? (
                            <>
                                {/* 侧边栏 */}
                                <Sidebar
                                    onDeleteSession={handleDeleteSession}
                                    isOpen={isSidebarOpen}
                                    onClose={() => setIsSidebarOpen(false)}
                                    historyList={sidebarHistory}
                                    onSelectHistory={handleSelectHistory}
                                    onNewChat={() => {
                                        goTo("main", { stack: "clear" });
                                        handleNewChat("main");
                                    }}
                                    hasHistory={hadHisList === 1}
                                    onLoadMore={() => fetchHistoryList(true)}
                                    isFetchingMore={isFetchingMore}
                                    hasMore={hasMoreHistory}
                                    displayUsername={displayUsername}
                                    onGoMyPage={() => {
                                        trackRecord(userId, "GO_USER_PAGE", "访问我的主页");
                                        goTo("user");
                                    }}
                                    onRenewVip={() => {
                                        setIsSidebarOpen(false);
                                        goTo("vip_renew");
                                    }}
                                    vipInfo={sidebarVipInfo}
                                    isVisitor={isVisitor}
                                    activeSessionId={mainSessionIdRef.current}
                                />
                                {/* 主对话页面 */}
                                <MainPage
                                    handleOpenSidebar={handleOpenSidebar}
                                    handleToggleCareMode={handleToggleCareMode}
                                    isCareMode={isCareMode}
                                    onGoToAgent={() => goTo("agent")}
                                    onGoToDoctorXu={handleGoToDoctorXu}
                                    onGoToVip={() => goTo("vip_renew", { from: "main" })}
                                    onGoToHealthMall={() => antdMessage.info("健康商城即将上线，敬请期待~")}
                                    scrollRef={messagesContainerRef}
                                    onScroll={checkScrollPosition}
                                    messages={mainMessages}
                                    fileList={fileList}
                                    setFileList={setFileList}
                                    handleSendMessage={handleSendMessage}
                                    onFeedback={onFeedback}
                                    handleFaqTypeWriter={handleFaqTypeWriter}
                                    scrollToBottom={scrollToBottom}
                                    handleLoading={setIsMainLoading}
                                    handleTry={handleTry}
                                    userId={userId}
                                    onOpenNutritionModal={() => setIsNutritionModalOpen(true)}
                                    showScrollToBottom={showScrollToBottom}
                                    isLoading={isMainLoading}
                                    buttonBottom={buttonBottom}
                                    handleStop={() => handleStop("main")}
                                    handleNewChat={() => handleNewChat("main")}
                                    onOpenScale={handleOpenScale}
                                    onOpenReport={() => handleOpenReport(false)}
                                    currentProfile={currentProfile}
                                    setCurrentProfile={setCurrentProfile}
                                    onGoToBasicInfo={(mode, profile) => {
                                        setBasicInfoMode(mode);
                                        if (profile) setCurrentProfile(profile);
                                        goTo("basic_info", { from: "main" });
                                    }}
                                    onBeforeVoiceToggle={handleBeforeVoiceToggle}
                                    onStopTTS={handleStopTTS}
                                    onDownload={handleDownload}
                                    onVipGuide={handleVipGuide}
                                />
                            </>
                        ) : currentPage === "agent" ? (
                            <>
                                {/* 侧边栏 */}
                                <Sidebar
                                    onDeleteSession={handleDeleteSession} isOpen={isSidebarOpen}
                                    onClose={() => setIsSidebarOpen(false)} historyList={sidebarHistory}
                                    onSelectHistory={handleSelectHistory} onNewChat={() => {
                                        goTo("main", { stack: "clear" });
                                        handleNewChat("main");
                                    }}
                                    hasHistory={hadHisList === 1} onLoadMore={() => fetchHistoryList(true)}
                                    isFetchingMore={isFetchingMore} hasMore={hasMoreHistory}
                                    displayUsername={displayUsername}
                                    onGoMyPage={() => {
                                        trackRecord(userId, "GO_USER_PAGE", "访问我的主页");
                                        goTo("user");
                                    }}
                                    onRenewVip={() => {
                                        setIsSidebarOpen(false);
                                        goTo("vip_renew");
                                    }}
                                    goLogin={() => {
                                        if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页
                                        setIsAuthorized(false);
                                    }}
                                    vipInfo={sidebarVipInfo}
                                    isVisitor={isVisitor}
                                    activeSessionId={agentSessionIdRef.current}
                                />
                                {/* AI广场 */}
                                <AgentPlaza
                                    userId={userId}
                                    handleToggleCareMode={() => handleToggleCareMode()}
                                    isCareMode={isCareMode}
                                    onOpenSidebar={handleOpenSidebar}
                                    onBack={() => goBack("main")}
                                    onOpenAbout={() => goTo("about")}
                                    onOpenNutrition={() => setIsNutritionModalOpen(true)}
                                    onSelectAgent={handleAgentSelect}
                                />
                            </>
                        ) : currentPage === "doctor_xu" ? (
                            // AI分身对话页面
                            <AgentPage
                                onOpenReport={() => handleOpenReport(true)}
                                onOpenScale={handleOpenScale}
                                onOpenNutritionModal={() => setIsNutritionModalOpen(true)}
                                fileList={fileList}
                                setFileList={setFileList}
                                scrollRef={agentContainerRef}
                                onBack={() => {
                                    goBack("main")
                                }}
                                handleSendMessage={handleSendMessage}
                                handleNewChat={() => handleNewChat("doctor_xu")}
                                isLoading={isAgentLoading}
                                messages={agentMessages}
                                handleStop={() => handleStop("doctor_xu")}
                                onFeedback={onFeedback}
                                handleFaqTypeWriter={handleFaqTypeWriter}
                                scrollToBottom={scrollToBottom}
                                handleLoading={setIsAgentLoading}
                                handleTry={handleTry}
                                userId={userId}
                                onScroll={checkScrollPosition}
                                showScrollToBottom={showScrollToBottom}
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onGoToBasicInfo={(mode, profile) => {
                                    setBasicInfoMode(mode); // 设定基础信息首屏状态：'list' | 'add' | 'edit'
                                    if (profile) setCurrentProfile(profile); // 联动刷新选中的咨询人
                                    goTo("basic_info", { from: "doctor_xu" }); // 路由跳转
                                }}
                                isTTSEnabled={isTTSEnabled}
                                toggleStreamingTTS={toggleStreamingTTS}
                                onBeforeVoiceToggle={handleBeforeVoiceToggle}
                                onStopTTS={handleStopTTS}
                                onDownload={handleDownload}
                                onVipGuide={handleVipGuide}
                            />
                        ) : currentPage === "scale" ? (
                            // 量表自测页面
                            <ScalePage userId={userId} onBack={() => goBack("agent")} />
                        ) : currentPage === "meal_plan" ? (
                            // 智能配餐页面
                            <MealPlan
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onBack={() => goBack("agent")} />
                        ) : currentPage === "basic_info" ? (
                            // 档案管理页面
                            <BasicInfo
                                mode={basicInfoMode}
                                currentProfile={currentProfile} // 传入当前选中的儿童档案，供修改页(edit)直接回显数据
                                onBack={() => goBack("agent")}
                            />
                        ) : currentPage === "food_query" ? (
                            // 食物查询页面
                            <FoodSearch userId={userId} onBack={() => goBack('agent')} />
                        ) : currentPage === "nutritionist" ? (
                            // 智能营养师对话页面
                            <NutritionistPage
                                onBack={() => goBack("agent")}
                                messages={nutritionMessages}
                                isLoading={isNutritionLoading}
                                handleSendMessage={handleSendMessage}
                                scrollRef={nutritionContainerRef}
                                userId={userId}
                                onFeedback={onFeedback}
                                handleStop={() => handleStop("nutritionist")}
                                onOpenNutritionModal={() => setIsNutritionModalOpen(true)}
                                handleTry={handleTry}
                                handleNewChat={() => handleNewChat("nutritionist")}
                                onOpenScale={handleOpenScale}
                                onOpenReport={() => handleOpenReport(false)}
                                fileList={fileList}
                                setFileList={setFileList}
                                // ✨【核心新增】：将全局状态和设置函数无缝下发给营养师页面
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onGoToBasicInfo={(mode, profile) => {
                                    setBasicInfoMode(mode);
                                    if (profile) setCurrentProfile(profile);
                                    goTo("basic_info", { from: "nutritionist" });
                                }}
                                isTTSEnabled={isTTSEnabled}
                                toggleStreamingTTS={toggleStreamingTTS}
                                onBeforeVoiceToggle={handleBeforeVoiceToggle}
                                onStopTTS={handleStopTTS}
                                onDownload={handleDownload}
                                onVipGuide={handleVipGuide}
                            />
                        ) : currentPage === "nutrition_assessment" ? (
                            // 营养评估页面
                            <NutritionAssessment
                                userId={userId}
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onBack={() => goBack("agent")}
                                onGoToSettings={handleGoToSettings}
                                isVipMember={isActiveVipMember(memberInfo)}
                                onRenewVip={() => goTo("vip_renew")}
                            />
                        ) : currentPage === "vip_renew" ? (
                            // 会员中心页面
                            <PersonalVIPPage
                                userId={userId}
                                displayUsername={displayUsername}
                                memberInfo={memberInfo}
                                onBack={() => goBack("main")}
                            />
                        ) : currentPage === "nutrition_manage" ? (
                            // 营养管理页面
                            <NutritionManage
                                userId={userId}
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onBack={() => goBack("agent")}
                                onOpenSportNutrition={() => goTo("sport_nutrition")}
                                onOpenGrowthDevelopment={() => goTo("growth_development")}
                                onGoToBasicInfo={(mode, profile) => {
                                    setBasicInfoMode(mode);
                                    if (profile) setCurrentProfile(profile);
                                    goTo("basic_info", { from: "nutrition_manage" });
                                }}
                            />
                        ) : currentPage === "sport_nutrition" ? (
                            // 运动营养页面
                            <SportNutrition
                                userId={userId}
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onBack={() => goBack("agent")}
                                onGoToBasicInfo={(mode, profile) => {
                                    setBasicInfoMode(mode);
                                    if (profile) setCurrentProfile(profile);
                                    goTo("basic_info", { from: "sport_nutrition" });
                                }}
                            />
                        ) : currentPage === "growth_development" ? (
                            // 成长发育页面
                            <GrowthDevelopment
                                userId={userId}
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onBack={() => goBack("agent")}
                                onGoToBasicInfo={(mode, profile) => {
                                    setBasicInfoMode(mode);
                                    if (profile) setCurrentProfile(profile);
                                    goTo("basic_info", { from: "growth_development" });
                                }}
                            />
                        ) : currentPage === "recipe_customization" ? (
                            // 食谱定制页面
                            <RecipeCustomization
                                userId={userId}
                                onBack={() => goBack("agent")}
                                isVipMember={isActiveVipMember(memberInfo)}
                                onRenewVip={() => goTo("vip_renew")}
                                //下发全局儿童档案上下文与多端状态分流函数
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onGoToBasicInfo={(mode, profile) => {
                                    setBasicInfoMode(mode); // 设定基础信息首屏状态：'list' | 'add' | 'edit'
                                    if (profile) setCurrentProfile(profile); // 维护档案时同步刷新全局选中的儿童
                                    goTo("basic_info", { from: "recipe_customization" }); // 记录来源页，确保从档案页返回时能回切到食谱定制页
                                }}
                            />
                        ) : currentPage === "ketogenic_diet" ? (
                            // 生酮饮食页面
                            <KetogenicDiet
                                userId={userId}
                                currentProfile={currentProfile}
                                setCurrentProfile={setCurrentProfile}
                                onBack={() => goBack("agent")}
                                onGoToBasicInfo={(mode, profile) => {
                                    setBasicInfoMode(mode);
                                    if (profile) setCurrentProfile(profile);
                                    goTo("basic_info", { from: "ketogenic_diet" });
                                }}
                            />
                        ) : (
                            // 个人中心页面
                            <UserPage
                                isVisitor={isVisitor}
                                username={displayUsername}
                                onBack={() => goBack("main")}
                                onLogout={() => {
                                    if (isVisitor) {
                                        // 如果是游客，直接"去登录"：即取消授权状态，让 LoginPage 重新渲染
                                        if (redirectToWxMiniLogin()) return; // 小程序内跳转原生登录页
                                        rememberResume();
                                        setIsAuthorized(false);
                                    } else {
                                        // 如果是正式用户，走原有的退出登录确认逻辑
                                        handleLogout();
                                    }
                                }}
                                onGoToBasicInfo={(mode) => {
                                    setBasicInfoMode(mode); // 设定基础信息首屏渲染为：'list' 档案列表视图
                                    goTo("basic_info", { from: "user" }); // 记录来源页，供基本信息页返回时回到个人中心
                                }}
                            />
                        )
                }
                />
            </Routes>

            {isHisLoading && (
                <div className="loader">
                    <Spin
                        spinning
                        fullscreen
                        indicator={<LoadingOutlined className="_icon" />}
                    />
                </div>
            )}
            {featureProfileLoading && <Spin spinning fullscreen />}
            {/* 档案确认弹窗 */}
            <FeatureProfileModal
                open={featureProfileModal.open}
                profiles={featureProfileModal.profiles}
                selectedKey={featureProfileModal.selectedKey}
                onSelectKey={(key) =>
                    setFeatureProfileModal((prev) => ({ ...prev, selectedKey: key }))
                }
                onOk={handleConfirmFeatureProfile}
                onCancel={() => setFeatureProfileModal(EMPTY_FEATURE_PROFILE_MODAL)}
            />
            {/* 营养信息录入弹窗 */}
            <NutritionModal
                open={isNutritionModalOpen}
                onCancel={handleNutritionCancel}
                onSubmit={handleNutritionSubmit}
            />
            {/* 食谱导出弹窗 */}
            {showExportPopup && (
                <ExportPopup
                    visible={showExportPopup}
                    onClose={() => setShowExportPopup(false)}
                    planCode={currentPlanCode}
                    onSelectExcel={() => download("xlsx")}
                    onSelectImage={() => download("png")}
                />
            )}
            {/* 下载加载遮罩 */}
            {isDownloadLoading && (
                <div className="loader">
                    <Spin
                        spinning
                        fullscreen
                        indicator={<LoadingOutlined className="_icon" />}
                    />
                </div>
            )}
        </div>
    );
}
